import { BaseObject } from './BaseObject';
import { SVGRenderer } from '../core/SVGRenderer';
import { Point2D } from '../types';

export class Angle extends BaseObject {
  public vertex: Point2D;
  public p1: Point2D;
  public p2: Point2D;
  public labelText: string;
  public isRightAngle: boolean;
  public radius: number = 28;

  private groupElem: SVGGElement | null = null;
  private pathElem: SVGPathElement | null = null;
  private textElem: SVGTextElement | null = null;

  constructor(
    renderer: SVGRenderer,
    vertex: Point2D,
    p1: Point2D,
    p2: Point2D,
    labelText: string = '',
    isRightAngle: boolean = false,
    id?: string
  ) {
    super(renderer, id);
    this.vertex = { ...vertex };
    this.p1 = { ...p1 };
    this.p2 = { ...p2 };
    this.labelText = labelText;
    this.isRightAngle = isRightAngle;
    this.color = renderer.config.highlightColor;
    this.render();
  }

  public render(): SVGElement {
    this.groupElem = this.renderer.createElement('g', {
      id: this.id,
      class: 'geom-angle',
    });

    const angle1 = SVGRenderer.angle(this.vertex, this.p1);
    const angle2 = SVGRenderer.angle(this.vertex, this.p2);

    if (this.isRightAngle) {
      // Draw standard perpendicular square marker
      const d1 = SVGRenderer.polarToCartesian(this.vertex, this.radius, angle1);
      const d2 = SVGRenderer.polarToCartesian(this.vertex, this.radius, angle2);
      const corner = {
        x: d1.x + (d2.x - this.vertex.x),
        y: d1.y + (d2.y - this.vertex.y),
      };

      const pathData = `M ${d1.x} ${d1.y} L ${corner.x} ${corner.y} L ${d2.x} ${d2.y}`;
      this.pathElem = this.renderer.createElement('path', {
        d: pathData,
        fill: 'rgba(245, 158, 11, 0.15)',
        stroke: this.color,
        'stroke-width': 2,
      });
    } else {
      // Arc angle marker - always use the shorter arc between the two directions
      const spanCW = (() => { let s = angle2 - angle1; while (s < 0) s += Math.PI*2; while (s >= Math.PI*2) s -= Math.PI*2; return s; })();
      const spanCCW = Math.PI * 2 - spanCW;
      const useSweepCW = spanCW <= spanCCW;
      const pathData = SVGRenderer.describeArcPath(this.vertex, this.radius, angle1, angle2, useSweepCW);
      this.pathElem = this.renderer.createElement('path', {
        d: pathData,
        fill: 'rgba(37, 99, 235, 0.1)',
        stroke: this.color,
        'stroke-width': 2,
      });
    }

    this.groupElem.appendChild(this.pathElem);

    if (this.labelText) {
      const midAngle = (angle1 + angle2) / 2;
      const labelPos = SVGRenderer.polarToCartesian(this.vertex, this.radius + 18, midAngle);

      this.textElem = this.renderer.createElement('text', {
        x: labelPos.x,
        y: labelPos.y,
        fill: this.color,
        'font-size': 13,
        'font-weight': 'bold',
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
      });
      this.textElem.textContent = this.labelText;
      this.groupElem.appendChild(this.textElem);
    }

    this.renderer.layers.highlights.appendChild(this.groupElem);
    this.element = this.groupElem;
    return this.groupElem;
  }

  public update(): void {
    if (!this.groupElem || !this.pathElem) return;
    const angle1 = SVGRenderer.angle(this.vertex, this.p1);
    const angle2 = SVGRenderer.angle(this.vertex, this.p2);

    if (this.isRightAngle) {
      const d1 = SVGRenderer.polarToCartesian(this.vertex, this.radius, angle1);
      const d2 = SVGRenderer.polarToCartesian(this.vertex, this.radius, angle2);
      const corner = {
        x: d1.x + (d2.x - this.vertex.x),
        y: d1.y + (d2.y - this.vertex.y),
      };
      this.pathElem.setAttribute('d', `M ${d1.x} ${d1.y} L ${corner.x} ${corner.y} L ${d2.x} ${d2.y}`);
    } else {
      const spanCW = (() => { let s = angle2 - angle1; while (s < 0) s += Math.PI*2; while (s >= Math.PI*2) s -= Math.PI*2; return s; })();
      const useSweepCW = spanCW <= Math.PI;
      this.pathElem.setAttribute('d', SVGRenderer.describeArcPath(this.vertex, this.radius, angle1, angle2, useSweepCW));
    }
  }
}
